import Add from '@mui/icons-material/Add';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Close from '@mui/icons-material/Close';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { alpha, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  getV1CompaniesCompanyIdMaterialTypeahead,
  getV1CompaniesCompanyIdSiteTypeahead,
  Material_Read_Typeahead,
  RateType,
  Site_Read_Typeahead,
  SiteState,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import AutocompleteEquipmentTypesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteEquipmentTypesTypeahead';
import AsyncAutocompleteField from '~components/FormFields/v2/Autocomplete/AsyncAutocompleteField';
import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import NumberInput from '~components/FormFields/v2/NumberInput';
import TextInput from '~components/FormFields/v2/TextInput';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { EquipmentTypeTypeahead } from '~hooks/useEquipment/models';
import { MaterialTypeahead } from '~hooks/useMaterials/models';
import { SiteTypeahead } from '~hooks/useSites/models';
import { getRateTypeLabel as getRateTypeLabelFromUtils } from '~pages/Settings/RatesManagement/rateUtils';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FreightRateCardError } from './applySiteRateSaveResult';
import FreightRatesSection from './FreightRatesSection';

interface FreightRateItemProps {
  index: number;
  displayNumber: number;
  // The row-number badge only means something once there's a second card
  // to number it against — a lone card's "1" is meaningless noise.
  showDisplayNumber: boolean;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  onDuplicate: () => void;
  onDelete: () => void;
  accountId?: string;
  projectId?: string;
  prevailingWage?: boolean;
  // The order's service date (`YYYY-MM-DD`), forwarded to the rates
  // section's fuel-surcharge rows. Absent on the project form.
  orderDate?: string;
  isDefault?: boolean;
  onToggleDefault?: () => void;
  // Per-card save error from a previous failed submit. Triggers red
  // border, auto-expand, and the inline error banner inside the
  // accordion. Cleared by the manager when the user edits any field
  // on the card.
  error?: FreightRateCardError;
  // Disables the delete icon while the manager is fetching the list of
  // orders affected by deleting this rate card (project scope only).
  // Avoids double-clicks during the brief network round-trip.
  isDeletePending?: boolean;
  scope?: 'order' | 'project';
  companyId?: string;
}

interface RateValue {
  rate?: number | null;
  type?: { label?: string; value?: string } | null;
}

type MenuAction = {
  icon: React.ReactElement;
  key: string;
  label: string;
  onClick: () => void;
};

const formatRateLabel = (
  label: string,
  rateValue: RateValue | null | undefined,
  currencyFormatter: (value: number) => string,
): string | null => {
  if (!rateValue) return null;

  const hasRate = rateValue.rate !== null && rateValue.rate !== undefined;
  const rateType = (rateValue.type?.value || rateValue.type?.label) as RateType;

  if (!hasRate && !rateType) return null;

  const typeLabel = rateType ? getRateTypeLabelFromUtils(rateType) : '';

  if (!hasRate) {
    return `${label}: ${typeLabel}`;
  }

  const displayValue = rateValue.rate ?? 0;
  const formattedValue =
    rateType === RateType.RATE_COMMISSION
      ? `${displayValue}%`
      : currencyFormatter(displayValue);

  return `${label}: ${formattedValue} ${typeLabel}`.trim();
};

const conditionLabel = (dotColor: string, text: string) => (
  <Typography
    component="span"
    sx={(th) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      fontSize: '10px',
      fontWeight: 700,
      color: th.brandV2.colors.treadBlack,
    })}
  >
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: dotColor,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
    {text}
  </Typography>
);

const FreightRateItem = ({
  index,
  displayNumber,
  showDisplayNumber,
  control,
  setValue,
  onDuplicate,
  onDelete,
  accountId,
  projectId,
  prevailingWage,
  orderDate,
  isDefault,
  onToggleDefault,
  error,
  isDeletePending,
  scope,
  companyId: formCompanyId,
}: FreightRateItemProps) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = formCompanyId || getEffectiveUserCompanyId(userStore);
  const { currencyFormatter } = useCompanyCurrency();

  const pickupSite = useWatch({
    control,
    name: `siteRates.${index}.pickupSite`,
  }) as AutocompleteFieldOption<SiteTypeahead> | null | undefined;

  const dropoffSite = useWatch({
    control,
    name: `siteRates.${index}.dropoffSite`,
  }) as AutocompleteFieldOption<SiteTypeahead> | null | undefined;

  const material = useWatch({
    control,
    name: `siteRates.${index}.material`,
  }) as AutocompleteFieldOption<MaterialTypeahead> | null | undefined;

  const equipmentType = useWatch({
    control,
    name: `siteRates.${index}.equipmentType`,
  }) as AutocompleteFieldOption<EquipmentTypeTypeahead> | null | undefined;

  const customerRate = useWatch({
    control,
    name: `siteRates.${index}.customerRate`,
  }) as RateValue | null | undefined;

  const vendorRate = useWatch({
    control,
    name: `siteRates.${index}.vendorRate`,
  }) as RateValue | null | undefined;

  const driverRate = useWatch({
    control,
    name: `siteRates.${index}.driverRate`,
  }) as RateValue | null | undefined;

  // Rows whose DELETE failed stay visible with `isDeleted: true` so the
  // next submit retries the deletion. We detect that state here and
  // swap the card's error banner copy to make the retry semantic obvious,
  // instead of styling the row identically to a normal failed create /
  // update.
  const rowIsDeleted = useWatch({
    control,
    name: `siteRates.${index}.isDeleted`,
  }) as boolean | undefined;
  const isFailedDelete = Boolean(rowIsDeleted && error?.rateError);

  const hasRateValue = (r: RateValue | null | undefined) =>
    (r?.rate !== null && r?.rate !== undefined) || Boolean(r?.type?.value);
  // Expansion is controlled (not left to the accordion's internal toggle)
  // so a fresh save error can force the accordion open even after the user
  // has manually collapsed it. Initial state preserves the legacy "open
  // when no rates set" rule, plus opens automatically if the row already
  // mounted in an error state. An SRS may have no rateError but still hold
  // one or more failed add-on POSTs / PATCHes / DELETEs — those still merit
  // the inline failure treatment so the user knows something on the card
  // needs attention.
  const addOnErrorCount = error
    ? Object.keys(error.addOnErrors.customer).length +
      Object.keys(error.addOnErrors.vendor).length +
      Object.keys(error.addOnErrors.driver).length
    : 0;
  const hasError = Boolean(error?.rateError) || addOnErrorCount > 0;
  const [expanded, setExpanded] = useState(
    () =>
      hasError ||
      (!hasRateValue(customerRate) &&
        !hasRateValue(vendorRate) &&
        !hasRateValue(driverRate)),
  );

  // MUI's AccordionSummary ButtonBase treats a Space keyup anywhere inside it
  // as "activate", including one that bubbles from this inline name field —
  // stopPropagation on the field's own key handlers doesn't reach far enough
  // up MUI's internals to prevent it. Refusing to collapse while the field is
  // focused sidesteps that regardless of which internal listener fires it.
  const isEditingNameRef = useRef(false);

  // Keep the accordion forced open while a save error is on this card.
  useEffect(() => {
    if (hasError) setExpanded(true);
  }, [hasError]);

  // Build rate summary for collapsed view
  const rateParts: string[] = [];
  const custLabel = formatRateLabel(
    t('project.form.rate_label_cust'),
    customerRate,
    currencyFormatter,
  );
  if (custLabel) rateParts.push(custLabel);
  const vendLabel = formatRateLabel(
    t('project.form.rate_label_vend'),
    vendorRate,
    currencyFormatter,
  );
  if (vendLabel) rateParts.push(vendLabel);

  const drvLabel = formatRateLabel(
    t('project.form.rate_label_driver'),
    driverRate,
    currencyFormatter,
  );
  if (drvLabel) rateParts.push(drvLabel);
  const rateSummary = rateParts.join(', ');

  // Visual palette for condition type chips and selector dots.
  // Colors from Material Design 50/600 swatches to distinguish the three condition types.
  const conditionColors = {
    pickupSite: '#E3F2FD', // light blue
    dropoffSite: '#E1F5FE', // lighter blue
    equipmentType: '#FFF3E0', // light orange
    material: '#E8F5E9', // light green
  } as const;

  const conditionDotColors = {
    pickupSite: '#1E88E5', // blue
    dropoffSite: '#0288D1', // darker blue
    equipmentType: '#F57C00', // orange
    material: '#43A047', // green
  } as const;

  // Condition chips for collapsed view
  const conditionChips: {
    label: string | React.ReactNode;
    color: string;
    key: string;
  }[] = [];
  if (pickupSite?.label && dropoffSite?.label) {
    // Combined pickup → dropoff chip
    conditionChips.push({
      key: 'sites',
      label: (
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {pickupSite.label}
          <ArrowForward sx={{ fontSize: 12 }} />
          {dropoffSite.label}
        </Box>
      ),
      color: conditionColors.pickupSite,
    });
  } else if (pickupSite?.label) {
    conditionChips.push({
      key: 'pickupSite',
      label: pickupSite.label,
      color: conditionColors.pickupSite,
    });
  } else if (dropoffSite?.label) {
    conditionChips.push({
      key: 'dropoffSite',
      label: dropoffSite.label,
      color: conditionColors.dropoffSite,
    });
  }
  if (equipmentType?.label) {
    conditionChips.push({
      key: 'equipmentType',
      label: equipmentType.label,
      color: conditionColors.equipmentType,
    });
  }
  if (material?.label) {
    conditionChips.push({
      key: 'material',
      label: material.label,
      color: conditionColors.material,
    });
  }

  const jurisdiction = useWatch({
    control,
    name: `siteRates.${index}.jurisdiction`,
  }) as string | null | undefined;

  const pwOnSiteRate = useWatch({
    control,
    name: `siteRates.${index}.pwOnSiteRate`,
  }) as number | null | undefined;

  const totalFringeRate = useWatch({
    control,
    name: `siteRates.${index}.totalFringeRate`,
  }) as number | null | undefined;

  // Track which condition selectors are visible
  // Initialize based on whether a value is already set
  const [visibleConditions, setVisibleConditions] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (pickupSite?.value) initial.add('pickupSite');
    if (dropoffSite?.value) initial.add('dropoffSite');
    if (material?.value) initial.add('material');
    if (equipmentType?.value) initial.add('equipmentType');
    return initial;
  });

  const [showPwFields, setShowPwFields] = useState(
    Boolean(jurisdiction || !isNil(pwOnSiteRate) || !isNil(totalFringeRate)),
  );

  // Sync visibility when form values change externally (e.g., form.reset from parent)
  useEffect(() => {
    setVisibleConditions((prev) => {
      const next = new Set(prev);
      if (pickupSite?.value) next.add('pickupSite');
      if (dropoffSite?.value) next.add('dropoffSite');
      if (material?.value) next.add('material');
      if (equipmentType?.value) next.add('equipmentType');
      return next.size === prev.size ? prev : next;
    });
  }, [pickupSite?.value, dropoffSite?.value, material?.value, equipmentType?.value]);

  useEffect(() => {
    if (jurisdiction || !isNil(pwOnSiteRate) || !isNil(totalFringeRate)) {
      setShowPwFields(true);
    }
  }, [jurisdiction, pwOnSiteRate, totalFringeRate]);

  const showCondition = useCallback((key: string) => {
    setVisibleConditions((prev) => new Set(prev).add(key));
  }, []);

  const hideCondition = useCallback(
    (key: string) => {
      setVisibleConditions((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setEditingConditions((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      // Clear the field value when hiding
      setValue(`siteRates.${index}.${key}`, null);
    },
    [setValue, index],
  );

  // Clicking a set condition's chip re-shows the search field pre-filled
  // with its current value, instead of the user having to delete the chip
  // and then reopen "Add condition" to pick the same condition type again
  // before they can search for a new value. The value is NOT cleared here —
  // abandoning the edit (leaving the field empty, or its own clear icon)
  // must not silently drop a condition the user never asked to remove; only
  // the chip's own delete icon (hideCondition) does that.
  const [editingConditions, setEditingConditions] = useState<Set<string>>(
    () => new Set(),
  );
  // Value each key held at the moment editing started, so the effect below
  // can tell "the user picked something (or cleared it) themselves" apart
  // from "the value is merely unchanged so far."
  const editSnapshotRef = useRef<Record<string, string | undefined>>({});

  const editCondition = useCallback((key: string, currentValue: string | undefined) => {
    editSnapshotRef.current[key] = currentValue;
    setEditingConditions((prev) => new Set(prev).add(key));
  }, []);

  // Once a field being edited resolves to something OTHER than the value it
  // held when editing started — a new pick, or an explicit clear via the
  // field's own icon — the edit is complete; collapse it out of edit mode.
  useEffect(() => {
    setEditingConditions((prev) => {
      if (prev.size === 0) return prev;
      const currentValues: Record<string, string | undefined> = {
        pickupSite: pickupSite?.value,
        dropoffSite: dropoffSite?.value,
        material: material?.value,
        equipmentType: equipmentType?.value,
      };
      const next = new Set(prev);
      prev.forEach((key) => {
        if (currentValues[key] !== editSnapshotRef.current[key]) next.delete(key);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [pickupSite?.value, dropoffSite?.value, material?.value, equipmentType?.value]);

  const siteFetchFn = {
    arguments: { companyId },
    request: async ({
      query,
      companyId,
    }: {
      query: Record<string, unknown>;
      companyId: string;
    }) => {
      const response = await getV1CompaniesCompanyIdSiteTypeahead({
        path: { 'company-id': companyId },
        query: {
          'search[query]': query['search[query]'] as string | undefined,
          'page[limit]': query['page[limit]'] as number | undefined,
          'page[after]': query['page[after]'] as string | undefined,
          'filter[states]': [SiteState.ACTIVE],
        },
      });
      return response;
    },
  };

  const parseSiteFn = (
    item: Site_Read_Typeahead,
  ): AutocompleteFieldOption<SiteTypeahead> => {
    const parsed = SiteTypeahead.parse(item);
    return { label: parsed.name, value: parsed.id, _entity: parsed };
  };

  const materialFetchFn = {
    arguments: { companyId },
    request: async ({
      query,
      companyId,
    }: {
      query: Record<string, unknown>;
      companyId: string;
    }) => {
      const response = await getV1CompaniesCompanyIdMaterialTypeahead({
        path: { 'company-id': companyId },
        query: {
          'search[query]': query['search[query]'] as string | undefined,
          'page[limit]': query['page[limit]'] as number | undefined,
          'page[after]': query['page[after]'] as string | undefined,
        },
      });
      return response;
    },
  };

  const parseMaterialFn = (
    item: Material_Read_Typeahead,
  ): AutocompleteFieldOption<MaterialTypeahead> => {
    const parsed = MaterialTypeahead.parse(item);
    return { label: parsed.name, value: parsed.id, _entity: parsed };
  };

  const rowActionsMenuHandler = useRef<MenuHandler>(null);
  const closeRowActionsMenu = useCallback(() => {
    rowActionsMenuHandler.current?.onClose?.();
  }, []);

  const addConditionMenuHandler = useRef<MenuHandler>(null);
  const closeAddConditionMenu = useCallback(() => {
    addConditionMenuHandler.current?.onClose?.();
  }, []);

  // A condition is "set" once it carries a selected value — it then
  // collapses into a removable chip instead of staying an open selector.
  const pickupSiteHasValue = Boolean(pickupSite?.value);
  const dropoffSiteHasValue = Boolean(dropoffSite?.value);
  const equipmentTypeHasValue = Boolean(equipmentType?.value);
  const materialHasValue = Boolean(material?.value);

  // A condition being actively re-picked (chip was clicked) stays out of
  // chip form and keeps its selector open, even though it still HasValue —
  // editCondition intentionally leaves the value in place until the user
  // actually picks a replacement (see editCondition's own comment).
  const pickupSiteShowsChip = pickupSiteHasValue && !editingConditions.has('pickupSite');
  const dropoffSiteShowsChip =
    dropoffSiteHasValue && !editingConditions.has('dropoffSite');
  const equipmentTypeShowsChip =
    equipmentTypeHasValue && !editingConditions.has('equipmentType');
  const materialShowsChip = materialHasValue && !editingConditions.has('material');

  const pickupSiteShowsSelector =
    (visibleConditions.has('pickupSite') && !pickupSiteHasValue) ||
    editingConditions.has('pickupSite');
  const dropoffSiteShowsSelector =
    (visibleConditions.has('dropoffSite') && !dropoffSiteHasValue) ||
    editingConditions.has('dropoffSite');
  const equipmentTypeShowsSelector =
    (visibleConditions.has('equipmentType') && !equipmentTypeHasValue) ||
    editingConditions.has('equipmentType');
  const materialShowsSelector =
    (visibleConditions.has('material') && !materialHasValue) ||
    editingConditions.has('material');

  const addableConditions: { key: string; label: string }[] = [];
  if (!visibleConditions.has('pickupSite') && !pickupSiteHasValue) {
    addableConditions.push({
      key: 'pickupSite',
      label: t('project.form.condition_pickup_site'),
    });
  }
  if (!visibleConditions.has('dropoffSite') && !dropoffSiteHasValue) {
    addableConditions.push({
      key: 'dropoffSite',
      label: t('project.form.condition_dropoff_site'),
    });
  }
  if (!visibleConditions.has('equipmentType') && !equipmentTypeHasValue) {
    addableConditions.push({
      key: 'equipmentType',
      label: t('project.form.condition_equipment_type'),
    });
  }
  if (!visibleConditions.has('material') && !materialHasValue) {
    addableConditions.push({
      key: 'material',
      label: t('project.form.condition_material'),
    });
  }

  const handleAddCondition = useCallback(
    (key: string) => {
      showCondition(key);
      closeAddConditionMenu();
    },
    [showCondition, closeAddConditionMenu],
  );

  // Primary actions (star toggle + duplicate) render above the divider;
  // delete renders below it in the app's danger color, per the row-header
  // action-cluster consolidation that replaced three separately floating
  // icon buttons prone to misclicks against the expand chevron.
  const primaryRowActions: MenuAction[] = useMemo(() => {
    const items: MenuAction[] = [];

    if (onToggleDefault) {
      items.push({
        icon: isDefault ? (
          <Star fontSize="small" sx={{ color: theme.brandV2.colors.treadBlue }} />
        ) : (
          <StarBorder fontSize="small" />
        ),
        key: 'toggle_default',
        label: isDefault
          ? t('project.form.remove_as_default_menu_item')
          : t('project.form.set_as_default_menu_item'),
        onClick: onToggleDefault,
      });
    }

    items.push({
      icon: <ContentCopyOutlined fontSize="small" />,
      key: 'duplicate',
      label: t('project.form.duplicate_rate_card_menu_item'),
      onClick: onDuplicate,
    });

    return items;
  }, [isDefault, onDuplicate, onToggleDefault, theme.brandV2.colors.treadBlue]);

  const handleRowActionClick = useCallback(
    (onClick: () => void) => {
      onClick();
      closeRowActionsMenu();
    },
    [closeRowActionsMenu],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Row-number badge — only shown once there's a second card to
          disambiguate against; a lone card's "1" is noise. Overlaps the
          card's top-left corner (design review request) instead of
          floating inline next to the chevron, which read as unaligned
          against the card edge. Rendered as a sibling of the Accordion,
          not a child — the Accordion's own overflow:hidden (needed to
          round AccordionDetails' bottom corners) clipped this badge where
          it pokes above the top edge. pointerEvents: 'none' keeps it from
          intercepting the click that toggles the accordion, since the
          badge visually overlaps the chevron's corner of the header. */}
      {showDisplayNumber && (
        <Box
          sx={(th) => ({
            position: 'absolute',
            top: -10,
            left: -10,
            minWidth: 20,
            height: 20,
            px: 0.5,
            borderRadius: '10px',
            backgroundColor: th.semantic.linkText,
            color: th.palette.common.white,
            fontSize: '11px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            pointerEvents: 'none',
          })}
        >
          {displayNumber}
        </Box>
      )}

      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => {
          // Don't let the user collapse a card that has an active error —
          // keeping it open puts the inline error reason in front of them.
          if (hasError && !isExpanded) return;
          // Don't let a keystroke in the inline name field collapse the card
          // out from under the user (see isEditingNameRef above).
          if (!isExpanded && isEditingNameRef.current) return;
          setExpanded(isExpanded);
        }}
        sx={(th) => ({
          border: `1px solid ${
            hasError ? th.palette.brandV2Red.main : theme.brandV2.colors.treadGray7
          }`,
          borderRadius: '8px !important',
          overflow: 'hidden',
          // Without this, the header (collapsed state + AccordionSummary)
          // inherits MUI Accordion's default Paper background, which
          // resolves to the same surfaceRaised the "Freight Rates" panel
          // around it already uses — dark mode's card and its own row
          // header were visually indistinguishable. treadGray7 matches the
          // color this same Dialog's own title bar already reads as in
          // dark mode — MUI tints an elevation-24 Paper's dark background
          // with a ~16.5% white overlay, landing within a few rgb units of
          // treadGray7. Light mode's Dialog paper gets no such overlay (the
          // effect is dark-mode-only), so the header and panel already
          // read as distinct there without any override — adding one just
          // introduced a flat gray band with no matching reference point.
          backgroundColor:
            th.palette.mode === 'dark' ? th.brandV2.colors.treadGray7 : undefined,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { marginTop: 0, marginBottom: '8px !important' },
          boxShadow: 'none',
          mb: 1,
        })}
      >
        <AccordionSummary
          sx={{
            minHeight: 48,
            '& .MuiAccordionSummary-content': {
              my: 1,
              alignItems: 'center',
              overflow: 'hidden',
              minWidth: 0,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              minWidth: 0,
              pr: 1,
              overflow: 'hidden',
            }}
          >
            {/* Chevron moved to the far left (design review request) —
                MUI's own expandIcon prop only renders on the far right,
                so this is a plain icon standing in for it, rotated by
                the same expanded state that already drives the accordion. */}
            <ExpandMore
              sx={{
                color: 'text.secondary',
                flexShrink: 0,
                mr: 1,
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
              }}
            />

            {/* Right column: Name, Rates, Conditions */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                overflow: 'hidden',
                gap: 0.5,
              }}
            >
              {/* Row 1: Name, editable in place */}
              <Controller
                name={`siteRates.${index}.name`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    placeholder={t('project.form.freight_rule_name_label')}
                    variant="outlined"
                    size="small"
                    // AccordionSummary toggles on a bubbled click; stopping it
                    // here keeps a plain click from collapsing the card.
                    onClick={(event) => event.stopPropagation()}
                    onFocus={(event) => {
                      event.stopPropagation();
                      isEditingNameRef.current = true;
                    }}
                    onBlur={() => {
                      field.onBlur();
                      isEditingNameRef.current = false;
                    }}
                    slotProps={{
                      input: {
                        sx: (th) => ({
                          fontWeight: 600,
                          fontSize: '14px',
                          color: 'text.primary',
                          // Matches the fill every other input on this card
                          // uses (the rate Type selects, the condition
                          // fields) in dark mode. Light mode's header is
                          // plain white (no shading override there — see
                          // the Accordion's own backgroundColor above), and
                          // surfaceSunken's light-mode value is a visible
                          // light-blue tint against it, so this field alone
                          // stays plain white to match its immediate
                          // background instead of the other inputs.
                          backgroundColor:
                            th.palette.mode === 'dark'
                              ? th.semantic.surfaceSunken
                              : th.palette.common.white,
                          '& input': { py: 0.75 },
                        }),
                      },
                    }}
                    sx={{ flex: 1, minWidth: 0, maxWidth: 320 }}
                  />
                )}
              />

              {/* Row 2: Rate summary — nothing renders until a rate is
                  actually set; a placeholder subtitle here was noise. */}
              {rateSummary && <Typography variant="body2">{rateSummary}</Typography>}

              {/* Row 3: Condition chips */}
              {conditionChips.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {conditionChips.map((chip) => (
                    <Chip
                      key={chip.key}
                      label={chip.label}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        backgroundColor: chip.color,
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          <Menu
            ref={rowActionsMenuHandler}
            highlightTriggerWhenOpen
            // Matches the Contact Type combobox's Autocomplete listbox
            // (src/components/FormFields/Parts/SelectOption.tsx) — opted in
            // here only, since the shared Menu component's own default
            // styling is relied on unmodified by ~40 other consumers
            // (status dropdowns, filters, export, row action menus, ...).
            elevation={4}
            // A plain object, not a theme-callback function: the shared
            // Menu component spreads this prop directly into MuiMenu's own
            // sx object (`{ ...sx, '& .MuiPaper-root': ... }`), which only
            // works for a plain object — a function form would spread to
            // nothing and silently drop these overrides.
            sx={{
              '& .MuiMenuItem-root': {
                minHeight: '48px',
                px: theme.spacing(1.5),
                fontSize: '12px',
                fontWeight: 500,
                borderLeft: '3px solid transparent',
                '&:hover, &.Mui-selected, &.Mui-focusVisible': {
                  backgroundColor: alpha(theme.brandV2.colors.treadYellow, 0.15),
                  borderLeftColor: theme.brandV2.colors.treadYellow,
                },
              },
            }}
            menuTrigger={
              <IconButton
                // Rendered as a span, not a button: AccordionSummary's own root is a
                // ButtonBase, and a nested <button> is invalid HTML that React reports as a
                // validateDOMNesting error. ButtonBase still applies role="button" and keeps
                // tabIndex plus its keydown handling, so this stays keyboard accessible.
                component="span"
                aria-label={t('project.form.rate_card_actions_aria_label')}
                sx={{
                  flexShrink: 0,
                  // 40x40 matches the app's standard icon-button touch target
                  // (see ProjectActionsMenu's kebab trigger).
                  width: 40,
                  height: 40,
                }}
              >
                <MoreHoriz sx={{ color: 'text.secondary' }} />
              </IconButton>
            }
          >
            {primaryRowActions.map((action) => (
              <MenuItem
                key={action.key}
                data-test-id={`freight-rate-item-action-${action.key}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleRowActionClick(action.onClick);
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {action.icon}
                  <Typography variant="body2">{action.label}</Typography>
                </Box>
              </MenuItem>
            ))}

            <Divider />

            <MenuItem
              data-test-id="freight-rate-item-action-delete"
              disabled={isDeletePending}
              onClick={(event) => {
                event.stopPropagation();
                if (!isDeletePending) {
                  handleRowActionClick(onDelete);
                }
              }}
              sx={{ color: theme.palette.brandV2Red.main }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteOutlined fontSize="small" sx={{ color: 'inherit' }} />
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  {t('project.form.delete_rate_card_menu_item')}
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </AccordionSummary>

        <AccordionDetails
          sx={(th) => ({
            backgroundColor: th.semantic.surfaceSunken,
            // The Accordion's own outer border already frames the left,
            // right, and bottom edges of this region (it wraps header +
            // details together) — this top edge is the only one that
            // border doesn't reach, so adding it here is what completes a
            // full border box around the expanded content instead of
            // doubling up a redundant border on the other three sides.
            borderTop: `1px solid ${th.brandV2.colors.treadGray7}`,
            // Gives the header/body color change some breathing room —
            // at pt: 0 the sunken background started immediately under
            // the header content with no gap.
            pt: 2,
            pb: 2,
          })}
        >
          {hasError && (
            <Box
              sx={(th) => ({
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: 'rgba(234, 18, 0, 0.08)',
                border: `1px solid ${th.palette.brandV2Red.main}`,
              })}
            >
              <Typography
                variant="body2"
                sx={(th) => ({
                  color: th.palette.brandV2Red.dark,
                  fontWeight: 600,
                })}
              >
                {/*
                  The card's red border already identifies which rate failed,
                  so the banner shows the actionable reason rather than a
                  generic "rate X failed to save" status:
                    - failed delete  → preserve the retry-on-resubmit copy
                    - 422            → surface the backend validation detail
                    - any other code → fall back to a contact-support string,
                                       since 5xx / network messages are not
                                       user-actionable on their own
                    - add-ons only   → keep the "N add-ons failed" count
                */}
                {isFailedDelete
                  ? t('project.form.rate_failed_to_delete')
                  : error?.rateError
                    ? error.rateErrorStatus === 422
                      ? error.rateError
                      : t('project.form.rate_save_failed_contact_support')
                    : t('project.form.rate_addon_save_failed', {
                        count: addOnErrorCount,
                      })}
              </Typography>
            </Box>
          )}
          {/* Rates section */}
          <Box sx={{ mb: 2 }}>
            <FreightRatesSection
              control={control}
              index={index}
              setValue={setValue}
              accountId={accountId}
              projectId={projectId}
              orderDate={orderDate}
              scope={scope}
              companyId={companyId}
            />
          </Box>

          {/* Conditions section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Conditions that already carry a value collapse into chips */}
            {(pickupSiteShowsChip ||
              dropoffSiteShowsChip ||
              equipmentTypeShowsChip ||
              materialShowsChip) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {pickupSiteShowsChip && (
                  <Chip
                    label={`${t('project.form.condition_pickup_site')}: ${pickupSite?.label}`}
                    // Clicking the chip body re-opens the search field
                    // (pre-filled with this value) to pick a different site
                    // directly; the delete icon remains the only way to
                    // drop the condition entirely.
                    onClick={() => editCondition('pickupSite', pickupSite?.value)}
                    onDelete={() => hideCondition('pickupSite')}
                    size="small"
                    sx={{
                      backgroundColor: conditionColors.pickupSite,
                      borderRadius: 1,
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  />
                )}
                {dropoffSiteShowsChip && (
                  <Chip
                    label={`${t('project.form.condition_dropoff_site')}: ${dropoffSite?.label}`}
                    onClick={() => editCondition('dropoffSite', dropoffSite?.value)}
                    onDelete={() => hideCondition('dropoffSite')}
                    size="small"
                    sx={{
                      backgroundColor: conditionColors.dropoffSite,
                      borderRadius: 1,
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  />
                )}
                {equipmentTypeShowsChip && (
                  <Chip
                    label={`${t('project.form.condition_equipment_type')}: ${equipmentType?.label}`}
                    onClick={() => editCondition('equipmentType', equipmentType?.value)}
                    onDelete={() => hideCondition('equipmentType')}
                    size="small"
                    sx={{
                      backgroundColor: conditionColors.equipmentType,
                      borderRadius: 1,
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  />
                )}
                {materialShowsChip && (
                  <Chip
                    label={`${t('project.form.condition_material')}: ${material?.label}`}
                    onClick={() => editCondition('material', material?.value)}
                    onDelete={() => hideCondition('material')}
                    size="small"
                    sx={{
                      backgroundColor: conditionColors.material,
                      borderRadius: 1,
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
            )}

            {/* Conditions being actively set — not yet given a value */}
            {pickupSiteShowsSelector ||
            dropoffSiteShowsSelector ||
            equipmentTypeShowsSelector ||
            materialShowsSelector ? (
              <Grid
                container
                spacing={2}
                sx={{
                  alignItems: 'center',
                }}
              >
                {pickupSiteShowsSelector && (
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      <AsyncAutocompleteField
                        control={control}
                        name={`siteRates.${index}.pickupSite`}
                        fetchFn={siteFetchFn}
                        parseFn={parseSiteFn}
                        queryName={`FreightRateItem_${index}_PickupSite`}
                        label={conditionLabel(
                          conditionDotColors.pickupSite,
                          t('project.form.condition_pickup_site'),
                        )}
                        disableClearable={false}
                        // Only rendered because the user just chose to set
                        // (or reset) this condition — take them straight
                        // into the dropdown instead of a field they then
                        // have to click into.
                        // oxlint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        onClick={() => hideCondition('pickupSite')}
                        sx={{ mb: 0.25 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                )}

                {dropoffSiteShowsSelector && (
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      <AsyncAutocompleteField
                        control={control}
                        name={`siteRates.${index}.dropoffSite`}
                        fetchFn={siteFetchFn}
                        parseFn={parseSiteFn}
                        queryName={`FreightRateItem_${index}_DropoffSite`}
                        label={conditionLabel(
                          conditionDotColors.dropoffSite,
                          t('project.form.condition_dropoff_site'),
                        )}
                        disableClearable={false}
                        // Only rendered because the user just chose to set
                        // (or reset) this condition — take them straight
                        // into the dropdown instead of a field they then
                        // have to click into.
                        // oxlint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        onClick={() => hideCondition('dropoffSite')}
                        sx={{ mb: 0.25 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                )}

                {equipmentTypeShowsSelector && (
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      <AutocompleteEquipmentTypesTypeahead
                        companyId={companyId}
                        control={control}
                        name={`siteRates.${index}.equipmentType`}
                        label={conditionLabel(
                          conditionDotColors.equipmentType,
                          t('project.form.condition_equipment_type'),
                        )}
                        disableClearable={false}
                        // Only rendered because the user just chose to set
                        // (or reset) this condition — take them straight
                        // into the dropdown instead of a field they then
                        // have to click into.
                        // oxlint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        onClick={() => hideCondition('equipmentType')}
                        sx={{ mb: 0.25 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                )}

                {materialShowsSelector && (
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      <AsyncAutocompleteField
                        control={control}
                        name={`siteRates.${index}.material`}
                        fetchFn={materialFetchFn}
                        parseFn={parseMaterialFn}
                        queryName={`FreightRateItem_${index}_Material`}
                        label={conditionLabel(
                          conditionDotColors.material,
                          t('project.form.condition_material'),
                        )}
                        disableClearable={false}
                        // Only rendered because the user just chose to set
                        // (or reset) this condition — take them straight
                        // into the dropdown instead of a field they then
                        // have to click into.
                        // oxlint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                      />
                      <IconButton size="small" onClick={() => hideCondition('material')}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : null}

            {/* Menu of condition types not yet set, behind a single trigger */}
            {addableConditions.length > 0 && (
              <Menu
                ref={addConditionMenuHandler}
                highlightTriggerWhenOpen
                elevation={4}
                // A plain object, not a theme-callback function — see the
                // matching comment on the row-actions Menu above.
                sx={{
                  '& .MuiMenuItem-root': {
                    minHeight: '48px',
                    px: theme.spacing(1.5),
                    fontSize: '12px',
                    fontWeight: 500,
                    borderLeft: '3px solid transparent',
                    '&:hover, &.Mui-selected, &.Mui-focusVisible': {
                      backgroundColor: alpha(theme.brandV2.colors.treadYellow, 0.15),
                      borderLeftColor: theme.brandV2.colors.treadYellow,
                    },
                  },
                }}
                menuTrigger={
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add fontSize="small" />}
                    sx={(th) => ({
                      alignSelf: 'flex-start',
                      // treadGray7 (the app's default outlined-button
                      // border) reads as nearly invisible against this
                      // card's sunken background. treadGray5 — matching
                      // "Add another rate" below it — plus a primary-text
                      // label and a hover fill give the trigger enough
                      // contrast to read as a clickable "add" affordance.
                      borderColor: th.brandV2.colors.treadGray5,
                      color: th.palette.text.primary,
                      fontSize: '12px',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: th.brandV2.colors.treadGray5,
                      },
                    })}
                  >
                    {t('project.form.add_condition_button')}
                  </Button>
                }
              >
                {addableConditions.map((cond) => (
                  <MenuItem
                    key={cond.key}
                    data-test-id={`freight-rate-item-add-condition-${cond.key}`}
                    onClick={() => handleAddCondition(cond.key)}
                  >
                    {cond.label}
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Box>

          {prevailingWage && (
            <Box sx={{ mt: 2 }}>
              {!showPwFields ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add fontSize="small" />}
                  onClick={() => setShowPwFields(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    borderColor: '#7B1FA2',
                    color: '#7B1FA2',
                    '&:hover': {
                      borderColor: '#7B1FA2',
                      backgroundColor: '#7B1FA214',
                    },
                  }}
                >
                  {t('form_fields.prevailing_wage_fields')}
                </Button>
              ) : (
                <Box
                  sx={{
                    border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '12px' }}
                    >
                      {t('form_fields.prevailing_wage_fields')}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setShowPwFields(false);
                        setValue(`siteRates.${index}.jurisdiction`, null);
                        setValue(`siteRates.${index}.pwOnSiteRate`, undefined);
                        setValue(`siteRates.${index}.totalFringeRate`, undefined);
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 4 }}>
                      <TextInput
                        control={control}
                        name={`siteRates.${index}.jurisdiction`}
                        label={t('form_fields.jurisdiction')}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <NumberInput
                        control={control}
                        name={`siteRates.${index}.pwOnSiteRate`}
                        label={t('form_fields.pw_on_site_rate')}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <NumberInput
                        control={control}
                        name={`siteRates.${index}.totalFringeRate`}
                        label={t('form_fields.total_fringe_rate')}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default FreightRateItem;
